// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { catchError, defer, finalize, forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { HceAlignment, HceAlignmentData, HceMolculeData } from '../../../interfaces/hce/hce-alignment-data';
import { compareKeysNumericFirstDesc, Utils } from '../hce-parser/utils';
import { HceParserService } from '../hce-parser/hce-parser.service';
import { HceRefGeneExon, HceRefGeneExonList, HceView } from '../../../interfaces/hce/hce-reference';
import { HceService } from '../hce.service';
import { HceAnnotation, HceBed } from '../../../interfaces/hce/hce-annotation';
import { HceSvColorList } from '../../../interfaces/hce/hce-sv-colors';
import { Analysis } from '../../../interfaces/analysis';
import { HceDataCacheService } from '../hce-data-cache/hce-data-cache.service';

@Injectable({ providedIn: 'root' })
export class HceAlignmentService {
  private static readonly CACHE_VERSION = 'v1';

  constructor(
    private readonly hce: HceService,
    private readonly parser: HceParserService,
    private readonly cache: HceDataCacheService,
  ) {}

  public getAlignmentData(
    analysis: Analysis,
    chromosomename: string,
    contigId: number | null,
    annotationByFile: Record<string, HceAnnotation[]>,
  ): Observable<any> {
    const analysisId = analysis.AnalysisID;
    const panelId = analysis.PanelID;
    const chrId = Utils.chromosome.toId(chromosomename);
    if (chrId == null) {
      throw new Error(`[HceAlignmentService] Invalid chromosome name: ${chromosomename}`);
    }
    const annotationFiles = Object.keys(annotationByFile);
    const cacheKey = this.makeCacheKey(analysisId, chrId, contigId, annotationFiles);
    return from(this.cacheGet(cacheKey)).pipe(
      switchMap(cached => {
        if (cached) {
          return of(cached);
        }
        return forkJoin({
          alns: this.hce.getSvEnhancedChrs(analysis),
          nmap: this.hce.getRefsNameMapping(panelId),
        }).pipe(
          switchMap(({ alns, nmap }) => forkJoin({
            alns: this.makeAlignmentData(alns[chrId], chrId),
            mlts: this.makeMultihits(analysis, alns, chrId, contigId),
            exon: this.makeRefGeneExonList(panelId, nmap),
            anno: this.makeAnnotationList(annotationByFile),
            refs: this.makeReference(panelId),
            nmap: of(nmap),
          })),
          map(({ alns, mlts, exon, anno, refs, nmap }) => ({
            ...alns,
            ...mlts,
            metadata: {
              ...alns.metadata,
              mappedRefNameTable: nmap,
            },
            ref_gene_exon: exon,
            ...refs,
            ...anno,
          })),
          switchMap(parsed => this.cachePut(cacheKey, parsed).pipe(
            map(() => parsed)
          ))
        );
      })
    );
  }

  private makeAlignmentData(
    data: HceAlignmentData,
    chromosomeId: number,
    contigId?: number | null,
  ): Observable<HceAlignmentData> {
    return of({
      consensus: this.normalizeConsensus(data.consensus, chromosomeId),
      provingMaps: this.normalizeProvingMaps(data.provingMaps, contigId),
      alignments: this.normalizeAlignments(data.alignments, contigId),
      metadata: data.metadata,
    });
  }

  private normalizeConsensus(
    consensus: HceMolculeData,
    chromosomeId: number
  ): HceMolculeData {
    return {
      ...consensus,
      name: `chr${Utils.chromosome.toName(chromosomeId)}`,
    };
  }

  private normalizeProvingMaps(
    provingMaps: Record<string, HceMolculeData>,
    contigId?: number | null
  ): Record<string, HceMolculeData> {
    if (contigId == null) {
      return provingMaps;
    }
    return Object.fromEntries(
      Object.entries(provingMaps).filter(
        ([, m]) => m.name === String(contigId)
      )
    );
  }

  private normalizeAlignments(
    alignments: Record<string, HceAlignment>,
    contigId?: number | null
  ): Record<string, HceAlignment> {
    // NOTE: Object.entries().sort() does not mutate the input object
    return Object.fromEntries(
      Object.entries(alignments)
        .filter(([, a]) => (contigId != null ? a.mapId == contigId : true))
        .sort(compareKeysNumericFirstDesc)
    );
  }

  private makeMultihits(
    analysis: Analysis,
    alignments: Record<number, HceAlignmentData>,
    chromosomeId: number,
    contigId?: number | null, 
  ) {
    console.time('Multihits: download+transform');
    const _compare = (a: string, b: number) => {
      const n = Utils.chromosome.toId(a);
      if (n == null) return false;
      return n !== b;
    }
    const _filter = (r: Record<string, number[]>, id: number) => {
      return Object.entries(r).filter(([k, _]) => _compare(k, id));
    }
    const _alignments = (alignments: Record<string, HceAlignment>, ids: number[]) => {
      const out: Record<string, HceAlignment> = {};
      for (const id of ids) {
        const hit = alignments[String(id)];
        if (hit) out[`aid_${id}`] = hit;
      }
      return out;
    }
    const _entry = (aligndata: HceAlignmentData, ids: number[]) => {
      return {
        consensus: aligndata.consensus,
        alignments: _alignments(aligndata.alignments, ids),
      };
    }
    const analysisId = analysis.AnalysisID;
    return this.makeMultihitsList(analysisId).pipe(
      switchMap(multihitslist => {
        if (contigId == null || !multihitslist[contigId]) {
          return of({ multihitslist, multihits: {} });
        }
        const targets = _filter(multihitslist[contigId], chromosomeId);
        if (targets.length === 0) {
          return of({ multihitslist, multihits: {} });
        }
        const requests = targets
          .map(([chrNm, alignmentIds]) => ({
            chrId: Utils.chromosome.toId(chrNm),
            chrNm,
            alignmentIds,
          }))
          .filter(({ chrId }) => chrId != null)
          .map(({ chrId, chrNm, alignmentIds }) => {
            return this.makeAlignmentData(alignments[chrId!], chrId!, contigId).pipe(
              map(aligndata => ([chrNm, _entry(aligndata, alignmentIds)])),
            );
          });
        return forkJoin(requests).pipe(
          map(entries => ({
            multihits: Object.fromEntries(entries),
            multihitslist,
          })),
        );
      }),
      finalize(() => console.timeEnd('Multihits: download+transform'))
    );
  }

  private makeMultihitsList(analysisId: number): Observable<Record<string, Record<string, number[]>>> {
    return this.hce.getMultihits(analysisId).pipe(
      map(multihits => {
        const out: Record<string, Record<string, number[]>> = {};
        for (const [id, contig] of Object.entries(multihits)) {
          const newContig: Record<string, number[]> = {};
          for (const [chr, data] of Object.entries(contig)) {
            const newKey = `chr${Utils.chromosome.toName(chr)}`;
            newContig[newKey] = data;
          }
          out[id] = newContig;
        }
        return out;
      })
    );
  }

  private makeReference(panelId: number) {
    console.time('Reference: download+transform');
    return this.hce.getView(panelId).pipe(
      map(records => {
        const chrNmList = this.parser.view.toChromosomeList(records);
        const reference = this.makeReferenceList(records, chrNmList);
        const refgroups = this.makeReferenceGroupList(reference, chrNmList);
        return { reference, refgroups };
      }),
      finalize(() => console.timeEnd('Reference: download+transform'))
    )
  }

  private makeReferenceList(records: HceView[], chrNmList: string[]) {
    const out = chrNmList.reduce<Record<string, any[]>>((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});
    records
      .filter(r => chrNmList.includes(r.title))
      .forEach(r => {
        const parsed = this.parser.view.parse(r);
        if (Array.isArray(parsed)) {
          out[r.title].push(...parsed);
        } else if (parsed != null) {
          out[r.title].push(parsed);
        }
      });
    return out;
  }

  private makeReferenceGroupList(
    referenceList: Record<string, any[]>,
    chrNmList: string[],
  ) {
    const out = chrNmList.reduce<Record<string, any[]>>((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});
    for (const [chrNm, reference] of Object.entries(referenceList)) {
      if (!chrNmList.includes(chrNm)) {
        continue;
      }
      let curName = '';
      let lastEnd = 0;
      const sorted = [...reference].sort((a, b) => (a.start - b.start));
      sorted.forEach((r, i) => {
        const tmpName = (r.name?.[0] ?? '').toLowerCase();
        const start = r.start as number;
        const end = r.end as number;
        if (i === 0) {
          out[chrNm].push({ start, end: null });
        } else if (i === sorted.length - 1) {
          const lastIdx = out[chrNm].length - 1;
          if (lastIdx >= 0) out[chrNm][lastIdx].end = end;
        } else if (
          (tmpName === 'p' && tmpName !== curName) ||
          (tmpName === 'q' && tmpName !== curName)
        ) {
          const lastIdx = out[chrNm].length - 1;
          if (lastIdx >= 0) out[chrNm][lastIdx].end = lastEnd;
          out[chrNm].push({ start, end: null });
        }
        curName = tmpName;
        lastEnd = end;
      });
    }
    return out;
  }

  private makeRefGeneExonList(
    panelId: number,
    refNameMap: Record<string, string>,
  ): Observable<HceRefGeneExonList> {
    console.time('RefGene exon/intron: download+transform');
    return this.hce.getRefGeneExonIntron(panelId).pipe(
      map(refGeneExonList => {
        const out = Object.values(refNameMap).reduce((acc, chrName) => {
          acc[chrName] = [] as HceRefGeneExon[];
          return acc;
        }, {} as HceRefGeneExonList);

        for (const row of refGeneExonList) {
          const id = Utils.chromosome.toId(row.chr);
          if (id === null) {
            continue;
          }
          const chrName = refNameMap[String(id)];
          if (!chrName || !out[chrName]) {
            continue;
          }
          out[chrName].push({
            id: row.id,
            direction: row.direction,
            start: Utils.number.toInt(row.start, 0),
            end: Utils.number.toInt(row.end, 0),
            exonLength: Utils.number.parseIntCsv(row.exonLength),
            exonStart: Utils.number.parseIntCsv(row.exonStart),
            exonCount: row.exonCount,
          });
        }
        return out;
      }),
      finalize(() => console.timeEnd('RefGene exon/intron: download+transform'))
    );
  }

  private makeAnnotationList(
    annotationByFile: Record<string, HceAnnotation[]>,
  ): Observable<{
    annotationList: Record<string, HceAnnotation[]>,
  }> {
    return of(annotationByFile).pipe(
      map(annotations => {
        const out: Record<string, HceAnnotation[]> = {};
        for (const [filename, parsed] of Object.entries(annotations)) {
          out[filename] = parsed.map(rec => ({
            ...rec,
            color: rec.color ?? this.resolveAnnotationColor(rec),
          }));
        }
        return out;
      }),
      map(annotations => ({
        annotationList: annotations,
        annoTxtColor: 'rgb(120,70,70)'
      })),
    );
  }

  private resolveAnnotationColor(rec: HceAnnotation): string {
    const colorMap = HceSvColorList as Record<string, string>;
    const types = Array.isArray(rec.type) ? rec.type : [rec.type];
    return (
      types.map(type => colorMap[type]).find(Boolean)
      ?? HceSvColorList.Other
    );
  }

  private makeCacheKey(
    analysisId: number,
    chromosomeId: number,
    contigId: number | null,
    annotations: string[],
  ): string {
    const v = HceAlignmentService.CACHE_VERSION;
    const url = new URL(`https://hce.cache/alignment/parsed/${v}`);
    const params = new URLSearchParams();
    params.set('analysisId', String(analysisId));
    params.set('chromosomeId', String(chromosomeId));
    if (contigId !== null) {
      params.set('contigId', String(contigId));
    }
    const sorted = [...new Set(annotations)].sort();
    sorted.forEach(annotation => {
      params.append('annotation', annotation);
    });
    url.search = params.toString();
    return url.toString();
  }

  private cacheGet(url: string): Observable<any | null> {
    return from(this.cache.get(url)).pipe(
      map(cached => {
        if (!cached) {
          return null;
        }
        try {
          return JSON.parse(cached);
        } catch (e) {
          console.warn('[HceAlignmentService] cache parse failed. Ignoring cache:', e);
          return null;
        }
      })
    );
  }

  private cachePut(url: string, parsed: any): Observable<void> {
    let body: string;
    try {
      body = JSON.stringify(parsed);
    } catch (e) {
      console.warn('[HceAlignmentService] cache stringify failed. Continuing without cache:', e);
      return of(void 0);
    }
    return defer(() => from(this.cache.put(url, body))).pipe(
      catchError((e) => {
        console.warn('[HceAlignmentService] cache write failed. Continuing without cache:', e);
        return of(void 0);
      })
    );
  }
}
